import type {
  AeropayBankAccount,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useMfa } from '@iheartjane/menu';
import type { KioskUser } from '@iheartjane/types';
import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import type {
  Cart,
  EmptyCart,
  Kiosk,
  Store,
  StoreSpecial,
} from '@jane/shared/types';
import { formatTime } from '@jane/shared/util';

import type {
  CreateCheckoutParams,
  RootLoaderType,
} from '../../../data-access';
import {
  getLocalCartUuid,
  useGetCart,
  useGetJanePayCart,
  useGetKiosk,
  useKioskAeropayUser,
  useKioskUser,
  useKioskUserLogout,
  useRemoveUserFromCart,
  useSubmitOrder,
} from '../../../data-access';
import { useTypedRouteLoaderData } from '../../../hooks';
import type { CheckoutResponse } from '../../../types';
import {
  formatBirthdate,
  resetSignInPrompt,
  trackLinkBankAccountTap,
  trackOrderSubmitted,
  trackSignedOut,
} from '../../../util';
import { useCartInfoForTracking } from '../CartInfoForTrackingProvider';
import { useIdleContext } from '../IdleProvider';
import { useProductTracking } from '../ProductTrackingProvider';
import { useSessionContext } from '../SessionProvider';
import { useEnsureContextWithinProvider } from '../hooks/useEnsureContextWithinProvider';
import { useLogoutUser } from '../hooks/useLogoutUser';

export const SCREEN_NAMES = {
  customerInfo: 'customer-info',
  linkBankCallout: 'link-bank-callout',
  linkBankPayment: 'link-bank-payment',
  loginCallout: 'login-callout',
  loginPayment: 'login-payment',
  loading: 'loading',
  mfa: 'mfa',
  payment: 'payment',
};

export type CheckoutFormFields = {
  'b-day-search': number;
  'checkout-notes'?: string;
  code?: string;
  'contact-num-search': string;
  'name-f-search': string;
  'name-l-search': string;
  'name-li-search': string;
};
export interface CheckoutContextType {
  anonymousCheckout: boolean;
  bankAccounts: AeropayBankAccount[];
  cart: Cart | EmptyCart;
  codeResent: boolean;
  error: string;
  errorSendingCode: string | null;
  errorVerifyingCode: string | null;
  formMethods: UseFormReturn<CheckoutFormFields>;
  handleContinueToMFAClick(): void;
  handleLinkBankClick(): void;
  handleLogout(): void;
  handleSubmit(): void;
  handleSubmitWithValidation(): void;
  isOrderLoading: boolean;
  janePayCartLoading: boolean;
  janePayOrderTotal: number;
  janePayServiceFeeAmount: number;
  kiosk: Kiosk | null;
  kioskUser: KioskUser | null;
  resendCode(): void;
  screen: string;
  selectedPayment: string;
  sendingCode: boolean;
  setAnonymousCheckout(anon: boolean): void;
  setError(error: string): void;
  setScreen(screen: string): void;
  setSelectedPayment: Dispatch<SetStateAction<string>>;
  store: Store;
  userPhone: string;
  verifyCode(code: string): Promise<void>;
  verifyingCode: boolean;
}

export const CheckoutContext = createContext<CheckoutContextType | null>(null);

export const CheckoutProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const { jdid } = useSessionContext();
  const { setTimerDisabled } = useIdleContext();
  const { setCartInfo } = useCartInfoForTracking();
  const {
    kiosk: initialKiosk,
    kioskId,
    storeId,
    store,
  } = useTypedRouteLoaderData<RootLoaderType>('root');

  const {
    canSendCode,
    codeResent,
    errorSendingCode,
    errorVerifyingCode,
    resendCode,
    sendCode,
    sendingCode,
    verifyCode,
    verifyingCode,
  } = useMfa();

  /** STATE CONTROLLERS */
  const [screen, setScreen] = useState(SCREEN_NAMES.customerInfo);
  const [anonymousCheckout, setAnonymousCheckout] = useState(
    store.kiosk_anonymous_checkout === 'required'
  );
  const [selectedPayment, setSelectedPayment] = useState('link');
  const [error, setError] = useState('');

  /** DATA ACCESS */
  const { data: kiosk } = useGetKiosk({
    kioskId,
    initialData: initialKiosk,
  });

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const {
    cartToppers: { cartTopperIds },
    inlineAds: { inlineAdIds },
  } = useProductTracking();
  const { data: cart = {} } = useGetCart();
  const showJanePay =
    !!store?.kiosk_allow_auth && !!store?.jane_pay_integration?.enabled;
  // Only fetch the Jane-Pay-optimized cart when the new fee-aware UI is on —
  // its only consumer (PaymentSelectionScreen) reads the totals behind the
  // same flag, so without this gate flag-off kiosks fire a redundant cart
  // request on entry to checkout and on every cart mutation.
  const showAchFees = useFlag(FLAGS.c3AchFeesOnKiosk);
  const { data: janePayCart = {}, isFetching: janePayCartLoading } =
    useGetJanePayCart(getLocalCartUuid(), {
      enabled: showJanePay && showAchFees,
    });
  const { data: kioskUser } = useKioskUser();
  const { data: aeropayData } = useKioskAeropayUser(
    {
      janeUserId: kioskUser?.id,
    },
    {
      // refetch for aeropay data to check for linked bank accounts
      refetchInterval:
        screen.includes('link-bank') || screen.includes('login') ? 5000 : false,
    }
  );
  /**
   * After successfully checking out, the user needs to be logged out from the
   * kiosk, but we do not want to remove their user ID from their cart. In this
   * case, we use the logout mutation directly instead of the hook. If the user
   * logs out while they are checking out, we use the logout function from the
   * hook (renamed logoutAndRemoveCartUser for clarity).
   */
  const { mutate: logout } = useKioskUserLogout({});
  const { logout: logoutAndRemoveCartUser } = useLogoutUser();
  const { mutate: removeUser } = useRemoveUserFromCart();

  /** FORM CONTEXT */
  const userFirstName = kioskUser?.first_name;
  const userLastName = kioskUser?.last_name;
  const userLastInitial = userLastName?.slice(0, 1);
  const userPhone = kioskUser?.phone;
  const userBirthDate = kioskUser?.birth_date
    ? formatTime({
        time: kioskUser?.birth_date,
        outputFormat: 'MMDDYYYY',
        inputFormat: 'YYYY-MM-DD',
      })
    : undefined;

  const defaultValues = useMemo(
    () =>
      kioskUser
        ? {
            'name-f-search': userFirstName ?? undefined,
            'name-l-search': userLastName ?? undefined,
            'name-li-search': userLastInitial ?? undefined,
            'contact-num-search': userPhone ?? '',
            'b-day-search': (userBirthDate as unknown as number) ?? undefined,
          }
        : undefined,
    [
      kioskUser,
      userFirstName,
      userLastName,
      userLastInitial,
      userPhone,
      userBirthDate,
    ]
  );
  const formMethods = useForm<CheckoutFormFields>({
    mode: 'onSubmit',
    defaultValues,
  });

  /** DEFINITIONS */
  const strictAnonymousCheckout = Boolean(
    store.kiosk_strict_anonymous_checkout
  );

  const bankAccounts = useMemo(
    () => aeropayData?.bank_accounts ?? [],
    [aeropayData]
  );

  const payingWithJanePay = useMemo(
    () =>
      bankAccounts.find(
        (account) => account.account_last_4 === selectedPayment
      ),
    [bankAccounts, selectedPayment]
  );

  const dmTypesInCart = useMemo(() => {
    const dmTypes = [];

    if (cartTopperIds.length > 0) dmTypes.push('bag');
    if (inlineAdIds.length > 0) dmTypes.push('in-line');

    return dmTypes;
  }, [cartTopperIds, inlineAdIds]);

  /** METHODS */
  const onOrderError = useCallback(
    (error: unknown, { birth_date, message, phone }: CreateCheckoutParams) => {
      trackOrderSubmitted({
        cart,
        kiosk,
        specials,
        store,
        trackingBag: {
          birthDate:
            anonymousCheckout || kiosk?.disable_birth_date_at_checkout
              ? null
              : birth_date,
          checkoutError: `${error}`,
          message: message || null,
          phoneNumber:
            anonymousCheckout || kiosk?.disable_phone_number_at_checkout
              ? null
              : phone,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod:
          payingWithJanePay && !anonymousCheckout ? 'jane pay' : null,
      });

      setError(String(error));
      setScreen(
        showJanePay && !anonymousCheckout
          ? SCREEN_NAMES.payment
          : SCREEN_NAMES.customerInfo
      );
      setTimerDisabled(false);
    },
    [
      cart,
      kiosk,
      specials,
      store,
      anonymousCheckout,
      dmTypesInCart,
      payingWithJanePay,
      showJanePay,
      setTimerDisabled,
    ]
  );

  const onOrderSuccess = useCallback(
    async (
      { reservation: { id, queue_number } }: CheckoutResponse,
      { birth_date, message, phone }: CreateCheckoutParams
    ) => {
      const phoneNumber =
        anonymousCheckout || kiosk?.disable_phone_number_at_checkout
          ? null
          : phone;
      const birthDate =
        anonymousCheckout || kiosk?.disable_birth_date_at_checkout
          ? null
          : birth_date;
      trackOrderSubmitted({
        cart,
        kiosk,
        store,
        specials,
        trackingBag: {
          birthDate: birth_date,
          checkoutError: null,
          message: message || null,
          phoneNumber,
        },
        dmType: dmTypesInCart.length === 0 ? null : dmTypesInCart,
        paymentMethod:
          payingWithJanePay && !anonymousCheckout ? 'jane pay' : null,
      });
      setCartInfo({
        cart,
        birthDate,
        phoneNumber,
        selectedBankAccount:
          bankAccounts.length > 0 && !anonymousCheckout
            ? bankAccounts.find(
                (account) => account.account_last_4 === selectedPayment
              )?.name ||
              bankAccounts.find((account) => account.is_selected)?.name ||
              bankAccounts[0].name
            : null,
        signedInOrder: !!kioskUser,
      });

      //storing the queue number in local storage to ensure that it persists on the Order Checkout page
      localStorage.setItem('queueNumber', queue_number);

      if (kioskUser) {
        logout();
      }

      navigate(`/stores/${storeId}/reservation/${id}/order-complete`);
      setTimerDisabled(false);
      resetSignInPrompt();
    },
    [
      anonymousCheckout,
      kiosk,
      cart,
      store,
      specials,
      dmTypesInCart,
      payingWithJanePay,
      setCartInfo,
      bankAccounts,
      kioskUser,
      navigate,
      storeId,
      setTimerDisabled,
      selectedPayment,
      logout,
    ]
  );

  const { mutate: submitOrder, isLoading } = useSubmitOrder({
    onError: onOrderError,
    onSuccess: onOrderSuccess,
  });

  const handleSubmit = useCallback(async () => {
    const [message, phone, firstName, lastName, lastInitial, birthDate] =
      formMethods.getValues([
        'checkout-notes',
        'contact-num-search',
        'name-f-search',
        'name-l-search',
        'name-li-search',
        'b-day-search',
      ]);

    setScreen(SCREEN_NAMES.loading);
    setTimerDisabled(true);
    //if the user is checking out anonymously, we update their cart to remove their user id
    if (!!kioskUser && strictAnonymousCheckout && anonymousCheckout) {
      removeUser({ uuid: cart.uuid });
    }

    const dateOfBirth =
      anonymousCheckout || kiosk?.disable_birth_date_at_checkout
        ? null
        : userBirthDate
          ? formatBirthdate(userBirthDate)
          : birthDate
            ? formatBirthdate(birthDate)
            : undefined;

    const name =
      strictAnonymousCheckout && anonymousCheckout
        ? 'ANONYMOUS CHECKOUT'
        : [
            userFirstName || firstName,
            userLastName || lastName || lastInitial,
          ].join(' ');

    const phoneNumber =
      anonymousCheckout || kiosk?.disable_phone_number_at_checkout
        ? ''
        : userPhone || phone;

    submitOrder({
      aeropay_account_verified: bankAccounts.length > 0,
      birth_date: dateOfBirth ?? undefined,
      cart_uuid: cart.uuid,
      current_url: window.location.href,
      j_device_id: jdid,
      message,
      name,
      phone: phoneNumber,
      products: cart.products,
      store_id: storeId,
      ...(payingWithJanePay &&
        !anonymousCheckout && {
          payment_method: 'jane_pay',
          aeropay_bank_account_id: payingWithJanePay.bank_account_id ?? null,
        }),
    });
  }, [
    anonymousCheckout,
    bankAccounts.length,
    cart.products,
    cart.uuid,
    formMethods,
    jdid,
    kiosk?.disable_birth_date_at_checkout,
    kiosk?.disable_phone_number_at_checkout,
    kioskUser,
    payingWithJanePay,
    removeUser,
    setTimerDisabled,
    storeId,
    strictAnonymousCheckout,
    submitOrder,
    userBirthDate,
    userFirstName,
    userLastName,
    userPhone,
  ]);

  const handleSubmitWithValidation = async () => {
    const isValid = await formMethods.trigger();

    if (isValid) {
      handleSubmit();
    }
  };

  const handleLogout = () => {
    setSelectedPayment('link');
    trackSignedOut({ source: ['Checkout'], store });
    logoutAndRemoveCartUser();
    formMethods.reset({
      'name-f-search': '',
      'name-l-search': '',
      'name-li-search': '',
      'contact-num-search': '',
      'b-day-search': '' as unknown as number,
    });
  };

  const handleLinkBankClick = () => {
    trackLinkBankAccountTap({
      deviceType: 'Kiosk',
      source: ['Checkout'],
      store,
    });

    if (!kioskUser) {
      setScreen(
        screen === SCREEN_NAMES.payment
          ? SCREEN_NAMES.loginPayment
          : SCREEN_NAMES.loginCallout
      );
    } else {
      setScreen(
        screen === SCREEN_NAMES.payment
          ? SCREEN_NAMES.linkBankPayment
          : SCREEN_NAMES.linkBankCallout
      );
    }
    setTimerDisabled(true);
  };

  const handleContinueToMFAClick = async () => {
    if (canSendCode()) sendCode();
    setTimerDisabled(true);
    setScreen(SCREEN_NAMES.mfa);
  };

  const handleVerifyCode = useCallback(
    async (code: string) => {
      const success = await verifyCode(code);
      if (success) {
        setTimerDisabled(false);
        handleSubmit();
      }
    },
    [verifyCode, setTimerDisabled, handleSubmit]
  );

  useEffect(() => {
    if (defaultValues && formMethods) {
      formMethods.reset(defaultValues);
    }
  }, [defaultValues, formMethods]);

  // Jane Pay (ACH) fee + fee-inclusive total from the Jane-Pay-optimized cart.
  // Falls back to the base cart total while the Jane Pay cart loads / when the
  // store does not pass the fee.
  const janePayServiceFeeAmount = janePayCart?.jane_pay_service_fee_amount ?? 0;
  const janePayOrderTotal = janePayCart?.order_total ?? cart?.order_total ?? 0;

  const context = useMemo(
    () => ({
      anonymousCheckout,
      bankAccounts,
      cart,
      janePayCartLoading,
      janePayOrderTotal,
      janePayServiceFeeAmount,
      codeResent,
      error,
      errorSendingCode,
      errorVerifyingCode,
      formMethods,
      handleContinueToMFAClick,
      handleLinkBankClick,
      handleLogout,
      handleSubmit,
      isOrderLoading: isLoading,
      kiosk: kiosk ?? null,
      kioskUser: kioskUser ?? null,
      resendCode,
      screen,
      selectedPayment,
      sendingCode,
      setAnonymousCheckout,
      setError,
      setScreen,
      setSelectedPayment,
      store,
      handleSubmitWithValidation,
      userPhone: userPhone ?? '',
      verifyCode: handleVerifyCode,
      verifyingCode,
    }),
    [
      anonymousCheckout,
      bankAccounts,
      cart,
      codeResent,
      error,
      errorSendingCode,
      errorVerifyingCode,
      formMethods,
      handleContinueToMFAClick,
      handleLinkBankClick,
      handleLogout,
      handleSubmit,
      isLoading,
      janePayCartLoading,
      janePayOrderTotal,
      janePayServiceFeeAmount,
      kiosk,
      kioskUser,
      resendCode,
      screen,
      selectedPayment,
      sendingCode,
      setError,
      setAnonymousCheckout,
      setScreen,
      store,
      setSelectedPayment,
      handleSubmitWithValidation,
      userPhone,
      handleVerifyCode,
      verifyingCode,
    ]
  );

  return (
    <CheckoutContext.Provider value={context}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () =>
  useEnsureContextWithinProvider(CheckoutContext, 'Checkout');
